import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useShortlists(orgId?: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["shortlists", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shortlists")
        .select("*, shortlist_items(user_id)")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createShortlist = useMutation({
    mutationFn: async (name: string) => {
      if (!user || !orgId) throw new Error("Missing context");
      const { data, error } = await supabase
        .from("shortlists")
        .insert({ name, org_id: orgId, created_by: user.id })
        .select()
        .single();
      if (error) throw error;

      try {
        await supabase.from("partner_audit_logs").insert({
          org_id: orgId,
          actor_user_id: user.id,
          action: "create_shortlist",
          entity_type: "shortlist",
          entity_id: data.id,
        });
      } catch (e) {
        console.error("Falha ao registrar auditoria:", e);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shortlists", orgId] });
    },
  });

  const deleteShortlist = useMutation({
    mutationFn: async (shortlistId: string) => {
      const { error } = await supabase.from("shortlists").delete().eq("id", shortlistId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shortlists", orgId] });
    },
  });

  const addItem = useMutation({
    mutationFn: async ({ shortlistId, userId }: { shortlistId: string; userId: string }) => {
      if (!user || !orgId) throw new Error("Missing context");
      const { error } = await supabase
        .from("shortlist_items")
        .insert({ shortlist_id: shortlistId, user_id: userId });
      if (error) throw error;

      try {
        await supabase.from("partner_audit_logs").insert({
          org_id: orgId,
          actor_user_id: user.id,
          action: "shortlist_add",
          entity_type: "profile",
          entity_id: userId,
          metadata: { shortlist_id: shortlistId },
        });
      } catch (e) {
        console.error("Falha ao registrar auditoria:", e);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shortlists", orgId] });
    },
  });

  const removeItem = useMutation({
    mutationFn: async ({ shortlistId, userId }: { shortlistId: string; userId: string }) => {
      if (!user || !orgId) throw new Error("Missing context");
      const { error } = await supabase
        .from("shortlist_items")
        .delete()
        .eq("shortlist_id", shortlistId)
        .eq("user_id", userId);
      if (error) throw error;

      try {
        await supabase.from("partner_audit_logs").insert({
          org_id: orgId,
          actor_user_id: user.id,
          action: "shortlist_remove",
          entity_type: "profile",
          entity_id: userId,
          metadata: { shortlist_id: shortlistId },
        });
      } catch (e) {
        console.error("Falha ao registrar auditoria:", e);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shortlists", orgId] });
    },
  });

  return {
    shortlists: query.data ?? [],
    isLoading: query.isLoading,
    createShortlist,
    deleteShortlist,
    addItem,
    removeItem,
  };
}
